import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import { ViewEntityButton } from "../preview/ViewEntityButton";
import { entityDeepLink, type EntityRef } from "./entityDeepLink";
import {
  entityDisplayLabelFromPreview,
  peekEntityPreview,
  prefetchEntityPreview,
  subscribeEntityPreviewResolved,
} from "../preview/entityPreviewData";
import {
  collectEntityRefsFromText,
  defaultEntityLinkLabel,
  parseEntityTags,
  type EntityTextScope,
} from "./entityTags";

export interface EntityRichTextProps {
  text: string;
  scope?: EntityTextScope;
  scopeLabels?: ScopeLabelCache;
  className?: string;
  prefetch?: boolean;
}

function resolveEntityLinkLabel(
  ref: EntityRef,
  tagLabel: string | null,
  scopeLabels?: ScopeLabelCache
): string {
  if (tagLabel?.trim()) return tagLabel.trim();

  if (ref.kind === "mission") {
    const fromScope = scopeLabels?.missionLabel(ref.scope.mission_id ?? ref.id);
    if (fromScope) return fromScope;
  }
  if (ref.kind === "task") {
    const fromScope = scopeLabels?.taskLabel(ref.scope.task_id ?? ref.id);
    if (fromScope) return fromScope;
  }

  const cached = peekEntityPreview(ref);
  if (cached) {
    const fromPreview = entityDisplayLabelFromPreview(ref, cached);
    if (fromPreview) return fromPreview;
  }

  return defaultEntityLinkLabel(ref.kind);
}

/**
 * Narrative text with inline entity tags (`{{artifact:ID|label}}`) rendered as
 * deep links with hover previews. Labels prefer tag text, then scope label
 * cache, then the shared entity preview cache.
 */
export function EntityRichText({
  text,
  scope = {},
  scopeLabels,
  className,
  prefetch = true,
}: EntityRichTextProps) {
  const [, setCacheTick] = useState(0);
  const segments = useMemo(() => parseEntityTags(text, scope), [text, scope]);
  const entityRefs = useMemo(() => collectEntityRefsFromText(text, scope), [text, scope]);

  useEffect(() => {
    if (!prefetch) return;
    for (const ref of entityRefs) {
      prefetchEntityPreview(ref);
    }
  }, [entityRefs, prefetch]);

  useEffect(() => subscribeEntityPreviewResolved(() => setCacheTick((n) => n + 1)), []);

  if (!text) return null;

  return (
    <span className={clsx("whitespace-pre-line", className)}>
      {segments.map((segment, index) => {
        if (segment.type === "text") {
          return <span key={`text-${index}`}>{segment.value}</span>;
        }

        const label = resolveEntityLinkLabel(segment.ref, segment.label, scopeLabels);
        const link = entityDeepLink(segment.ref);
        if (!link) {
          return (
            <span key={`entity-${index}`} className="text-indigo-300">
              {label}
            </span>
          );
        }

        return (
          <ViewEntityButton
            key={`entity-${index}-${segment.ref.kind}-${segment.ref.id}-${segment.ref.version ?? ""}`}
            entity={{ ...segment.ref, title: label }}
            label={label}
            variant="inline"
            className="mx-0.5 align-baseline"
          />
        );
      })}
    </span>
  );
}
